import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Trash2,
  ArrowDownLeft,
  Download,
} from 'lucide-react';
import type {
  ClaimTransaction,
  CreateTransactionRequest,
  UpdateTransactionRequest,
} from '@/api/generated/client/models';
import { formatCentsSafe, labelFor } from '@/lib/utils';
import { AddPaymentModal } from './AddPaymentModal';

// ── Display label maps ────────────────────────────────

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  carrier_payment: 'Carrier Payment',
  supplement_payment: 'Supplement Payment',
  depreciation_release: 'Depreciation Release',
  mortgage_release: 'Mortgage Release',
  other: 'Other',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  check: 'Check',
  ach: 'ACH',
  wire: 'Wire',
  other: 'Other',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Expected',
  received: 'Received',
  deposited: 'Deposited',
  cleared: 'Cleared',
  voided: 'Void',
};

// ── Props ─────────────────────────────────────────────

export interface PaymentsTabProps {
  transactions: ClaimTransaction[];
  onCreate: (data: CreateTransactionRequest, checkFile?: File) => void | Promise<void>;
  onUpdate: (id: number, data: UpdateTransactionRequest, checkFile?: File, removeExistingCheckFile?: boolean) => void | Promise<void>;
  onDelete: (id: number) => void;
  onDownloadCheckFile?: (transactionId: number, filename?: string) => void;
  isCreating: boolean;
  isUpdating: boolean;
  editingId: number | null;
  setEditingId: (id: number | null) => void;
  canWrite: boolean;
}

// ── Component ─────────────────────────────────────────

export function PaymentsTab({
  transactions,
  onCreate,
  onUpdate,
  onDelete,
  onDownloadCheckFile,
  isCreating,
  isUpdating,
  editingId,
  setEditingId,
  canWrite,
}: PaymentsTabProps) {
  const [modalOpen, setModalOpen] = useState(false);

  const editingTransaction = useMemo(
    () => (editingId != null ? transactions.find((t) => t.id === editingId) ?? null : null),
    [editingId, transactions],
  );

  const openAdd = () => {
    if (!canWrite) return;
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (t: ClaimTransaction) => {
    if (!canWrite) return;
    setEditingId(t.id);
    setModalOpen(true);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setEditingId(null);
    }
    setModalOpen(open);
  };

  const handleCreate = (data: CreateTransactionRequest, checkFile?: File) => {
    onCreate(data, checkFile);
    setModalOpen(false);
  };

  const handleUpdate = (id: number, data: UpdateTransactionRequest, checkFile?: File, removeExistingCheckFile?: boolean) => {
    onUpdate(id, data, checkFile, removeExistingCheckFile);
    setModalOpen(false);
  };

  return (
    <div className="space-y-2">
      {/* Add button */}
      <Button size="sm" className="w-full h-8 text-xs" onClick={openAdd} disabled={!canWrite}>
        <Plus className="h-3 w-3 mr-2" /> Add Payment
      </Button>

      {/* Payment list */}
      <div className="space-y-2">
        {transactions.length === 0 ? (
          <p className="text-center py-4 text-xs text-muted-foreground">No payments yet</p>
        ) : (
          transactions.map((t) => (
            <div key={t.id} className={`text-xs border-2 rounded-md px-2.5 py-2 bg-card group transition-all space-y-0.5 ${canWrite ? 'cursor-pointer hover:border-primary hover:shadow-sm' : ''}`} onClick={() => canWrite && openEdit(t)}>
              {/* Row 1: Type + Actions */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 min-w-0">
                  <ArrowDownLeft className="h-3 w-3 text-green-600 shrink-0" />
                  <span className="font-medium truncate">
                    {PAYMENT_TYPE_LABELS[t.transactionType] || labelFor(t.transactionType)}
                  </span>
                </div>
                <div className="flex items-center gap-0.5">
                  {t.checkFileBlobId && (
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-5 w-5 text-muted-foreground hover:text-primary opacity-0 group-hover:opacity-100 transition-opacity shrink-0" 
                      onClick={(e) => { e.stopPropagation(); onDownloadCheckFile?.(t.id, t.checkFileName ?? undefined); }} 
                      title="Download check file"
                    >
                      <Download className="h-3 w-3" />
                    </Button>
                  )}
                  {canWrite && (
                    <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity shrink-0" onClick={(e) => { e.stopPropagation(); onDelete(t.id); }} title="Delete">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              {/* Row 2: Date · Payer · Method */}
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground min-w-0">
                <span>{t.transactionDate}</span>
                {(t.payer || t.payee) && <span className="truncate">· {t.payer || t.payee}</span>}
                {t.paymentMethod && <span>· {PAYMENT_METHOD_LABELS[t.paymentMethod] || labelFor(t.paymentMethod)}</span>}
              </div>
              {/* Row 3: Amount | Status */}
              <div className="flex items-center justify-between pt-0.5">
                <span className="text-sm font-semibold tabular-nums text-green-600">
                  +{formatCentsSafe(t.amountCents)}
                </span>
                <Badge variant="secondary" className="text-[10px] h-4 px-1 shrink-0">
                  {STATUS_LABELS[t.status] || t.status}
                </Badge>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      <AddPaymentModal
        open={modalOpen}
        onOpenChange={handleClose}
        onSubmit={handleCreate}
        onUpdate={handleUpdate}
        isSubmitting={isCreating || isUpdating}
        editingTransaction={editingTransaction}
      />
    </div>
  );
}
